/* eslint-disable prettier/prettier */
import ZBreadCrumbs from "@/shared/components/custom/breadcrumbs";
import ErrorBoundary from "@/shared/components/error-boundary";
import { ZTableDeprecrated } from "@/shared/components/table-deprecrated/TableDeprecrated";
import { ROUTES } from "@/shared/constants/routes";
import { cn } from "@nextui-org/react";
/* eslint-disable @typescript-eslint/no-unsafe-enum-comparison */
import { LoadingSpinner } from "@/shared/components/custom/spin";
import { ZTablePagination } from "@/shared/components/TablePagination";
import { SearchField } from "@/shared/components/TableSearch";
import { usePagination } from "@/shared/hooks/usePagination";
import { useSearch } from "@/shared/hooks/useSearch";
import { useSort } from "@/shared/hooks/useSort";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import { Outlet } from "react-router-dom";
import { AllVendorsListColumns } from "../constants/columns";
import { useGetVendorsListQuery } from "../requests";
import { VendorDetails } from "../types";

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(relativeTime);

const AllVendorsList: React.FC = () => {
  const { data: allVendorsList, isFetching } = useGetVendorsListQuery();

  const { paginationState, setPaginationState, resetPageNumber } = usePagination();

  const { searchTerm, setSearchTerm, filteredData: searchFilteredData } = useSearch<VendorDetails>(allVendorsList || [], {
    searchBy: (vendor) => [vendor.vendor_product_name],
    resetPageNumber
  });

  const { currentPage, rowsPerPage } = paginationState;

  const totalVendors = searchFilteredData?.length ?? 0;
  const totalPages = Math.ceil(totalVendors / rowsPerPage);

  const { sortedData, setSortModel } = useSort<VendorDetails>(
    searchFilteredData || [],
    { column: "", direction: "ascending" }, 
    { paginationState }
  );

  const paginatedVendors = sortedData?.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage
  ) ?? [];

  return (
    <ErrorBoundary fallback={null}>
      <div className="w-full h-full flex flex-col">
        <div className="flex items-center pb-4 justify-between min-h-[44px] max-h-[44px]">
          <div className="flex gap-4 items-center">
            <ZBreadCrumbs
              items={[{ title: "Vendors", href: `/${ROUTES.VENDORS}` }]}
            />
          </div>
        </div>
        <div className="h-full w-full overflow-hidden rounded-lg flex flex-col">
          <div className="px-6 py-4 flex flex-col bg-white gap-2 border-b border-border">
            {/* Vendors header */}
            <div className="flex items-center grow justify-between w-full">
              <div className="flex items-center grow flex-1 gap-2">
                <p className="text-sm font-medium leading-5 text-[#171717]">
                  All Vendors
                </p>
                <div className="p-1 bg-[#F4F4F5] px-2 rounded-lg">
                  <p className="text-[10px] text-[#52525B] leading-4 font-medium">
                    {allVendorsList?.length ?? 0}
                  </p>
                </div>
              </div>
              <SearchField value={searchTerm}
                onChange={setSearchTerm}
                placeholder="Search by vendor or product name" />
              <div className="flex-1 overflow-auto relative" />
            </div>
          </div>
          <div className="grow overflow-hidden flex">
            <div className="flex-1 flex flex-col bg-white overflow-hidden p-4">
              <div className="flex-1 flex flex-col overflow-hidden">
                <ZTableDeprecrated<VendorDetails>
                  className={cn("border-1 border-[#E4E4E7] rounded-lg overflow-auto scroll-auto h-full")}
                  layout="fixed"
                  loading
                  rows={paginatedVendors || []}
                  columns={AllVendorsListColumns}
                  isHeaderSticky
                  isCompact
                  removeWrapper
                  getRowId={(row) => row.uid}
                  tableHeaderProps={{ className: "shadow-none rounded-none " }}
                  tableRowProps={(row, rowIndex) => ({
                    className: cn(
                      rowIndex === 0 ? "border-none" : "border-y",
                      "group",
                    ),
                  })}
                  classNames={{
                    wrapper: "!shadow-none !rounded-none",
                  }}
                  aria-label="Vendors List"
                  onSortModelChange={setSortModel}
                  tableBodyProps={{
                    emptyContent: isFetching ? (
                      <div className="flex items-center justify-center">
                        <LoadingSpinner
                          containerClassName="bg-transparent relative"
                          className="mx-auto"
                        />
                      </div>
                    ) : (
                      "No vendors found"
                    ),
                  }}
                />
                <ZTablePagination
                  totalPages={totalPages}
                  paginationState={paginationState}
                  setPagination={setPaginationState}
                />
              </div>
            </div>
          </div>
        </div>
        <div className="grow overflow-hidden rounded-xl">
          <Outlet />
        </div>
      </div>
    </ErrorBoundary>
  );
};

export default AllVendorsList;
